import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { base64ToFile } from 'ngx-image-cropper';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { validationMessage } from '@constants/constant';
import { API_URLS } from '@core/api-url.enum';
import { Application, BucketFolders } from '@enums/application.enums';
import { Messages } from '@enums/messages.enums';
import { VideoTrimComponent } from '@modules/dashboard/components/new-post/newposts/video-trim/video-trim.component';
import { processVideoFile } from '@modules/dashboard/utils/common-utils';
import { VIDEO_UPLOAD_CONSTANTS } from '@modules/dynamic-form-builder/components/video-upload/video-upload.constants';
import { CommonService } from '@modules/shared/services/common.service';
import { UploadService } from '@services/upload.service';
import { loadVideoMetadataV2 } from '@utils/common.utils';
import { environment } from '@environments/environment';
import { buildCoverPageImageFileName } from '@modules/whitelabel-cover-page/cover-page-upload.utils';

@Component({
  selector: 'app-video-upload',
  templateUrl: './video-upload.component.html',
  styleUrls: ['./video-upload.component.scss']
})
export class VideoUploadComponent implements OnInit, OnDestroy {
  @Input() field: any;
  @Input() form: FormGroup;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  readonly application = Application;
  readonly CONST = VIDEO_UPLOAD_CONSTANTS;
  videoPreviewUrl = '';
  posterPreviewUrl = '';
  percent = Application.ZERO;
  showProgress = false;

  private videoObjectUrl = '';
  private destroy$ = new Subject<void>();

  constructor(
    public readonly cs: CommonService,
    private readonly uploadService: UploadService,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: BsModalService
  ) {}

  get posterFieldName(): string {
    return this.field?.posterFieldName || VIDEO_UPLOAD_CONSTANTS.POSTER_FIELD_NAME;
  }

  get uploadTitle(): string {
    return (
      this.field?.bannerImageType2Title ||
      this.field?.videoUploadTitle ||
      VIDEO_UPLOAD_CONSTANTS.DEFAULT_TITLE
    );
  }

  get selectLabel(): string {
    return (
      this.field?.bannerImageType2Label ||
      this.field?.videoUploadSelectLabel ||
      VIDEO_UPLOAD_CONSTANTS.DEFAULT_SELECT_LABEL
    );
  }

  get currentVideoPreviewSrc(): string {
    const value = this.form?.get(this.field?.name)?.value;
    return this.videoPreviewUrl || (typeof value === 'string' ? value : '');
  }

  get currentPosterPreviewSrc(): string {
    const value = this.form?.get(this.posterFieldName)?.value;
    return this.posterPreviewUrl || (typeof value === 'string' ? value : '');
  }

  get hasPreview(): boolean {
    return !!(
      this.showProgress ||
      this.currentVideoPreviewSrc ||
      this.form?.get(this.field?.name)?.value
    );
  }

  ngOnInit(): void {
    this.ensurePosterControl();
    const existingVideo = this.form?.get(this.field?.name)?.value;
    if (typeof existingVideo === 'string' && existingVideo) {
      this.videoPreviewUrl = existingVideo;
    }
    const existingPoster = this.form?.get(this.posterFieldName)?.value;
    if (typeof existingPoster === 'string' && existingPoster) {
      this.posterPreviewUrl = existingPoster;
    }
  }

  ngOnDestroy(): void {
    this.revokeVideoObjectUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }

  mobileFileChangeEvent(event: unknown): void {
    this.handleVideoFileSelection(this.extractMobileFile(event));
  }

  fileChangeEvent(event: Event): void {
    if (!this.cs.isBrowser) {
      return;
    }
    const input = event.target as HTMLInputElement;
    this.handleVideoFileSelection(input.files?.[0] || null);
  }

  removeVideo(clearPoster = false): void {
    this.form.get(this.field.name)?.reset();
    if (clearPoster) {
      this.form.get(this.posterFieldName)?.reset();
      this.posterPreviewUrl = '';
    }
    this.revokeVideoObjectUrl();
    this.videoPreviewUrl = '';
    this.showProgress = false;
    this.percent = Application.ZERO;
  }

  private extractMobileFile(event: unknown): File | null {
    const firstItem = Array.isArray(event) ? event[0] : event;
    if (firstItem instanceof File) {
      return firstItem;
    }
    if (firstItem && typeof firstItem === 'object' && 'file' in firstItem) {
      const file = (firstItem as { file?: unknown }).file;
      return file instanceof File ? file : null;
    }
    return null;
  }

  private handleVideoFileSelection(file?: File | null): void {
    if (!this.cs.isBrowser || !file) {
      return;
    }

    if (!this.cs.isValidAudioVideo(file, 'video')) {
      this.cs.showErrorMessage(Messages.INVALID_FILE_TYPE);
      this.removeVideo();
      return;
    }

    const maxSize = this.field?.fileSizeLimit || Application.VIDEO_FILE_SIZE_LIMIT;
    if (file.size > maxSize) {
      this.cs.showErrorMessage(
        validationMessage.FILE_SIZE_LIMIT.replace(
          '[fileSize]',
          String(
            Math.round(
              maxSize /
                (Application.ONE_THOUSAND_TWENTY_FOUR *
                  Application.ONE_THOUSAND_TWENTY_FOUR)
            )
          )
        )
      );
      this.removeVideo();
      return;
    }

    loadVideoMetadataV2(file)
      .then((metadata) => {
        const videoMetadata = metadata as { videoMode?: string };
        const trimObj = processVideoFile(file);
        this.revokeVideoObjectUrl();
        this.videoObjectUrl = trimObj.file;
        this.videoPreviewUrl = trimObj.file;
        this.openVideoTrimModal(
          trimObj,
          videoMetadata.videoMode || Application.LANDSCAPE
        );
      })
      .catch(() => {
        this.cs.showErrorMessage(Messages.SOMETHING_WENT_WRONG);
        this.removeVideo();
      });
  }

  private openVideoTrimModal(
    videoData: { file: string; videoFile: File },
    videoMode: string
  ): void {
    const initialState = {
      video: videoData,
      videoMode,
      isWatermark: false
    } as unknown as Partial<VideoTrimComponent>;

    const modalRef = this.modalService.show(VideoTrimComponent, {
      initialState,
      ignoreBackdropClick: true,
      class: 'video-trim-modal',
      keyboard: false
    });

    const contentComponent = modalRef.content as VideoTrimComponent;
    contentComponent?.confirmed
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: { src?: string }) => {
        this.ensurePosterControl();
        this.form.patchValue({ [this.field.name]: videoData.videoFile });
        this.uploadVideoPoster(data?.src || '');
      });

    contentComponent?.cancelled.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.removeVideo(true);
    });
  }

  private ensurePosterControl(): void {
    if (!this.form.get(this.posterFieldName)) {
      this.form.addControl(this.posterFieldName, new FormControl(''));
    }
  }

  private uploadVideoPoster(posterDataUrl: string): void {
    if (!posterDataUrl) {
      this.form.patchValue({ [this.posterFieldName]: '' });
      this.posterPreviewUrl = '';
      this.cdr.detectChanges();
      return;
    }

    const blob = base64ToFile(posterDataUrl);
    const uploadFolder = this.field?.uploadFolder ?? BucketFolders.createPostImageFolder;
    const posterFileName = this.field?.uploadFolder
      ? buildCoverPageImageFileName(`${this.posterFieldName}.png`)
      : `${Application.POST + Date.now()}_${this.posterFieldName}.png`;
    const posterFile = new File([blob], posterFileName, { type: 'image/png' });
    if (!this.cs.isValidFile(posterFile)) {
      this.cs.showErrorMessage(Messages.INVALID_FILE_TYPE);
      this.removeVideo();
      return;
    }

    this.posterPreviewUrl = posterDataUrl;
    this.showProgress = true;
    this.uploadService
      .uploadFileData(
        API_URLS.uploadFile,
        {
          file: posterFile,
          fileName: posterFileName,
          folder: uploadFolder
        },
        true
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.progressStatus === Application.UPLOADING) {
            this.percent = response.progress;
            this.cdr.detectChanges();
            return;
          }
          if (response.progressStatus === Application.COMPLETED) {
            this.showProgress = false;
            this.percent = Application.ZERO;
            const uploadedUrl =
              response.data ||
              environment.s3Config.bucketUrl + uploadFolder + posterFileName;
            this.form.patchValue({ [this.posterFieldName]: uploadedUrl });
            this.posterPreviewUrl = uploadedUrl;
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.showProgress = false;
          this.percent = Application.ZERO;
          this.cs.showErrorMessage(Messages.SOMETHING_WENT_WRONG);
        }
      });
  }

  private revokeVideoObjectUrl(): void {
    if (this.videoObjectUrl) {
      URL.revokeObjectURL(this.videoObjectUrl);
      this.videoObjectUrl = '';
    }
  }
}
